import { readItemField } from '../compile/item-field-reader';
import type { generatorReaders } from './random-generator';

type Readers = ReturnType<typeof generatorReaders>;
type Row = Record<string, unknown>;
export interface LootSpec { item: string; template: string; name?: string; flavor?: string; qty: number; value: number }
const firearms = ['firearms_25_caliber_bullets', 'firearms_300_caliber_bullets', 'firearms_40_caliber_bullets', 'firearms_50_caliber_bullets', 'firearms_mortar_shells'];
const families = ['arrows', 'crossbow_bolts', 'sling_bullets', 'blowgun_needles', 'firearms', 'great_arrows'];
const uuid = (value: unknown): string => String(value ?? '').split('|')[0]!.trim().replace(/-/g, '').toUpperCase();

/** Ordinary level zero omits the zero-valued charge member, as the old template producer did. */
export function scrollLoot(read: Readers, values: Row, die: (sides: number) => number,
    template: (parts: Record<string, string[]>) => string, scrollId: string): LootSpec {
    const entered = String(values.random_scroll_spell_level ?? '').trim();
    const level = entered !== '' && Number.isFinite(Number(entered)) ? Math.trunc(Number(entered)) : die(3);
    const pool = eligibleScrollSpells(read, level);
    if (!pool.length) throw new Error('No spells available for the given scroll level.');
    const spell = pool[die(pool.length) - 1]!;
    return { item: scrollId, template: template({ ...(level ? { item_charges: [String(level)] } : {}), charged_spell: [String(readItemField(spell, 'item_id'))] }),
        name: `Scroll of ${String(readItemField(spell, 'item_name'))}`, qty: 1, value: Math.max(25, level * level * 25) };
}
export function eligibleScrollSpells(read: Readers, level: number): Row[] {
    return read.all('spell').filter(row => read.available(row) && read.ungated(row)
        && read.value(row, 'spell_level') != null && Number(read.value(row, 'spell_level')) === level
        && !['consumes_ability_charges', 'add_charges_to_ability', 'requires_class', 'requires_subclass'].some(key => read.has(row, key)));
}

/** Macro weights are independent of species counts. Each failed firearm attempt redraws its caliber. */
export function ammoLoot(read: Readers, values: Row, classifications: Row[], die: (sides: number) => number): LootSpec {
    const raw = values.random_ammo_homebrew ?? 1;
    const iconic = raw === true || Math.trunc(Number(raw)) === 1;
    const requested = String(values.random_ammo_family ?? 'weighted').trim();
    const mode = families.includes(requested) ? requested : 'weighted';
    if (mode === 'great_arrows' && iconic) throw new Error('Great arrows are only in the base ammo pool. Choose Base ammo or Weighted mix.');
    const pool = read.all('ammo').filter(row => read.available(row) && Number(readItemField(row, 'homebrew') ?? 0) === Number(iconic));
    for (let attempt = 0; attempt < 24; attempt++) {
        let family = mode;
        if (family === 'weighted') {
            const weights = iconic ? [337, 232, 189, 137, 105] : [320, 220, 180, 130, 100, 50];
            let draw = die(weights.reduce((a, b) => a + b, 0));
            family = families[weights.findIndex(weight => (draw -= weight) <= 0)]!;
        }
        if (family === 'firearms') family = firearms[die(firearms.length) - 1]!;
        const classification = classifications.find(row => readItemField(row, 'variable_name') === family);
        if (!classification) continue;
        const prefix = family.startsWith('firearms_') ? 'Bullet of ' : ({ arrows: 'Arrow of ', crossbow_bolts: 'Bolt of ', sling_bullets: 'Sling Pellet of ', blowgun_needles: 'Blowgun Needle of ' } as Record<string, string>)[family];
        const candidates = pool.filter(row => iconic
            ? !!prefix && String(readItemField(row, 'item_name') ?? '').toLowerCase().startsWith(prefix.toLowerCase())
            : read.ids(row, 'weapon_ammo_classification').some(id => uuid(id) === uuid(readItemField(classification, 'item_id'))));
        if (candidates.length) {
            const selected = candidates[die(candidates.length) - 1]!;
            return { item: String(readItemField(selected, 'item_id')), template: '', qty: 1, value: read.price(selected) };
        }
    }
    throw new Error('No ammo items match the current pool (or classification is empty).');
}
